import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

// ── Type Definitions ──────────────────────────────────────────

interface PageRegistryEntry {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface PageAccessRule {
  id: string;
  pageId: string;
  principalType: string;
  principalId: string;
  subcommitteeRole: string | null;
  accessLevel: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ApiError {
  statusCode?: number;
  message?: string;
}

type PrincipalType = 'User' | 'Role' | 'Subcommittee';
type AccessLevel = 'View' | 'Edit' | 'None';

const PRINCIPAL_TYPES: PrincipalType[] = ['User', 'Role', 'Subcommittee'];
const ACCESS_LEVELS: AccessLevel[] = ['View', 'Edit', 'None'];
const ROLE_OPTIONS = ['Admin', 'Organizer', 'Delegate', 'Attendee'];

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  success: {
    color: '#2e7d32',
    backgroundColor: '#e8f5e9',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  pageList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  pageItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  pageItemSelected: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    backgroundColor: '#e8f5e9',
    borderLeft: '3px solid #1a6b3c',
  },
  pageInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.2rem',
    flex: 1,
  },
  pageName: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#0f2d1f',
  },
  pageDescription: {
    fontSize: '0.8rem',
    color: '#666',
  },
  badge: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#fff',
    marginLeft: '0.75rem',
    whiteSpace: 'nowrap' as const,
  },
  badgeCustom: {
    backgroundColor: '#1a6b3c',
  },
  badgeDefault: {
    backgroundColor: '#999',
  },
  detailPanel: {
    marginTop: '1.5rem',
    padding: '1rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
  },
  detailTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1a6b3c',
    marginBottom: '0.5rem',
  },
  detailDescription: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '0.75rem',
  },
  groupHeading: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginTop: '1rem',
    marginBottom: '0.5rem',
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '0.25rem',
  },
  ruleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fff',
    borderRadius: '4px',
    marginBottom: '0.4rem',
    border: '1px solid #e8e8e8',
    fontSize: '0.85rem',
  },
  ruleInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.15rem',
    flex: 1,
  },
  ruleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  select: {
    padding: '0.3rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '0.8rem',
    backgroundColor: '#fff',
  },
  input: {
    padding: '0.3rem 0.5rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '0.8rem',
    width: '160px',
  },
  btnPrimary: {
    padding: '0.35rem 0.75rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDanger: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  btnDangerDisabled: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#ccc',
    color: '#888',
    fontSize: '0.75rem',
    cursor: 'not-allowed',
  },
  addRuleForm: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid #c9a84c',
  },
  formTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.5rem',
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  label: {
    fontSize: '0.8rem',
    color: '#333',
    minWidth: '90px',
  },
  noRules: {
    fontSize: '0.85rem',
    color: '#999',
    fontStyle: 'italic',
    marginTop: '0.5rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function PageAccessControlSettings() {
  const [pages, setPages] = useState<PageRegistryEntry[]>([]);
  const [rulesCount, setRulesCount] = useState<Record<string, number>>({});
  const [selectedPage, setSelectedPage] = useState<PageRegistryEntry | null>(null);
  const [rules, setRules] = useState<PageAccessRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [subcommittees, setSubcommittees] = useState<{ id: string; name: string }[]>([]);

  // Add rule form state
  const [newPrincipalType, setNewPrincipalType] = useState<PrincipalType>('Role');
  const [newPrincipalId, setNewPrincipalId] = useState('');
  const [newSubcommitteeRole, setNewSubcommitteeRole] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState<AccessLevel>('View');
  const [addingRule, setAddingRule] = useState(false);

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchPages();
    fetchSubcommittees();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      fetchRules(selectedPage.slug);
    } else {
      setRules([]);
    }
  }, [selectedPage]);

  async function fetchPages() {
    try {
      const data = await apiClient<PageRegistryEntry[]>('/page-access');
      setPages(data);
      const counts: Record<string, number> = {};
      await Promise.all(
        data.map(async (page) => {
          try {
            const r = await apiClient<PageAccessRule[]>(
              `/page-access/${page.slug}/rules`,
            );
            counts[page.id] = r.length;
          } catch {
            counts[page.id] = 0;
          }
        }),
      );
      setRulesCount(counts);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to load pages.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSubcommittees() {
    try {
      const data = await apiClient<{ id: string; name: string }[]>('/subcommittees');
      setSubcommittees(data);
    } catch {
      setSubcommittees([]);
    }
  }

  function getSubcommitteeName(id: string): string {
    const sc = subcommittees.find((s) => s.id === id);
    return sc ? sc.name : id;
  }

  async function fetchRules(slug: string) {
    setRulesLoading(true);
    try {
      const data = await apiClient<PageAccessRule[]>(`/page-access/${slug}/rules`);
      setRules(data);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to load rules.');
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }

  function handleSelectPage(page: PageRegistryEntry) {
    setSelectedPage(page.id === selectedPage?.id ? null : page);
    setError('');
    setSuccessMsg('');
    setConfirmDeleteId(null);
  }

  async function handleAddRule() {
    if (!selectedPage) return;
    if (!newPrincipalId.trim()) {
      setError('Principal is required.');
      return;
    }
    setAddingRule(true);
    setError('');
    try {
      const body: Record<string, string> = {
        principalType: newPrincipalType,
        principalId: newPrincipalId.trim(),
        accessLevel: newAccessLevel,
      };
      if (newPrincipalType === 'Subcommittee' && newSubcommitteeRole.trim()) {
        body.subcommitteeRole = newSubcommitteeRole.trim();
      }
      await apiClient(`/page-access/${selectedPage.slug}/rules`, {
        method: 'POST',
        body,
      });
      setSuccessMsg('Rule added successfully.');
      setNewPrincipalId('');
      setNewSubcommitteeRole('');
      setNewAccessLevel('View');
      await fetchRules(selectedPage.slug);
      // Update rules count
      setRulesCount((prev) => ({
        ...prev,
        [selectedPage.id]: (prev[selectedPage.id] || 0) + 1,
      }));
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to add rule.');
    } finally {
      setAddingRule(false);
    }
  }

  async function handleUpdateAccessLevel(ruleId: string, accessLevel: string) {
    if (!selectedPage) return;
    setError('');
    try {
      await apiClient(`/page-access/${selectedPage.slug}/rules/${ruleId}`, {
        method: 'PATCH',
        body: { accessLevel },
      });
      setSuccessMsg('Access level updated.');
      await fetchRules(selectedPage.slug);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to update rule.');
    }
  }

  async function handleDeleteRule(ruleId: string) {
    if (!selectedPage) return;
    setError('');
    try {
      await apiClient(`/page-access/${selectedPage.slug}/rules/${ruleId}`, {
        method: 'DELETE',
      });
      setSuccessMsg('Rule deleted.');
      setConfirmDeleteId(null);
      await fetchRules(selectedPage.slug);
      setRulesCount((prev) => ({
        ...prev,
        [selectedPage.id]: Math.max((prev[selectedPage.id] || 1) - 1, 0),
      }));
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      setError(apiErr.message || 'Failed to delete rule.');
      setConfirmDeleteId(null);
    }
  }

  // Group rules by principalType
  function groupRules(rulesList: PageAccessRule[]) {
    const groups: Record<string, PageAccessRule[]> = {
      User: [],
      Role: [],
      Subcommittee: [],
    };
    for (const rule of rulesList) {
      if (groups[rule.principalType]) {
        groups[rule.principalType].push(rule);
      } else {
        groups[rule.principalType] = [rule];
      }
    }
    return groups;
  }

  function renderRuleRow(rule: PageAccessRule) {
    const isConfirmingDelete = confirmDeleteId === rule.id;
    const displayName = rule.principalType === 'Subcommittee'
      ? getSubcommitteeName(rule.principalId)
      : rule.principalId;

    return (
      <div key={rule.id} style={styles.ruleRow}>
        <div style={styles.ruleInfo}>
          <span style={{ fontWeight: 500 }}>
            {displayName}
            {rule.subcommitteeRole && (
              <span style={{ color: '#666', fontWeight: 400 }}>
                {' '}(Role: {rule.subcommitteeRole})
              </span>
            )}
          </span>
          {rule.isDefault && (
            <span style={{ fontSize: '0.7rem', color: '#c9a84c' }}>Default rule</span>
          )}
        </div>
        <div style={styles.ruleActions}>
          <select
            style={styles.select}
            value={rule.accessLevel}
            onChange={(e) => handleUpdateAccessLevel(rule.id, e.target.value)}
            aria-label={`Access level for ${rule.principalId}`}
          >
            {ACCESS_LEVELS.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          {isConfirmingDelete ? (
            <>
              <button
                style={styles.btnDanger}
                onClick={() => handleDeleteRule(rule.id)}
              >
                Confirm
              </button>
              <button
                style={{ ...styles.btnPrimary, backgroundColor: '#666' }}
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              style={rule.isDefault ? styles.btnDangerDisabled : styles.btnDanger}
              disabled={rule.isDefault}
              onClick={() => setConfirmDeleteId(rule.id)}
              title={rule.isDefault ? 'Cannot delete default rules' : 'Delete rule'}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  function renderAddRuleForm() {
    return (
      <div style={styles.addRuleForm}>
        <div style={styles.formTitle}>Add Rule</div>
        <div style={styles.formRow}>
          <span style={styles.label}>Principal Type:</span>
          <select
            style={styles.select}
            value={newPrincipalType}
            onChange={(e) => {
              setNewPrincipalType(e.target.value as PrincipalType);
              setNewPrincipalId('');
              setNewSubcommitteeRole('');
            }}
            aria-label="Principal type"
          >
            {PRINCIPAL_TYPES.map((pt) => (
              <option key={pt} value={pt}>{pt}</option>
            ))}
          </select>
        </div>
        <div style={styles.formRow}>
          <span style={styles.label}>Principal:</span>
          {newPrincipalType === 'Role' ? (
            <select
              style={styles.select}
              value={newPrincipalId}
              onChange={(e) => setNewPrincipalId(e.target.value)}
              aria-label="Role selector"
            >
              <option value="">-- Select Role --</option>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          ) : newPrincipalType === 'Subcommittee' ? (
            <select
              style={styles.select}
              value={newPrincipalId}
              onChange={(e) => setNewPrincipalId(e.target.value)}
              aria-label="Subcommittee selector"
            >
              <option value="">-- Select Subcommittee --</option>
              {subcommittees.map((sc) => (
                <option key={sc.id} value={sc.id}>{sc.name}</option>
              ))}
            </select>
          ) : (
            <input
              style={styles.input}
              type="text"
              value={newPrincipalId}
              onChange={(e) => setNewPrincipalId(e.target.value)}
              placeholder="User ID"
              aria-label="Principal ID"
            />
          )}
        </div>
        {newPrincipalType === 'Subcommittee' && (
          <div style={styles.formRow}>
            <span style={styles.label}>Subcmte Role:</span>
            <input
              style={styles.input}
              type="text"
              value={newSubcommitteeRole}
              onChange={(e) => setNewSubcommitteeRole(e.target.value)}
              placeholder="e.g. Chair, Member"
              aria-label="Subcommittee role"
            />
          </div>
        )}
        <div style={styles.formRow}>
          <span style={styles.label}>Access Level:</span>
          <select
            style={styles.select}
            value={newAccessLevel}
            onChange={(e) => setNewAccessLevel(e.target.value as AccessLevel)}
            aria-label="Access level"
          >
            {ACCESS_LEVELS.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <button
            style={styles.btnPrimary}
            onClick={handleAddRule}
            disabled={addingRule}
          >
            {addingRule ? 'Adding...' : 'Add Rule'}
          </button>
        </div>
      </div>
    );
  }

  function renderRulesPanel() {
    if (!selectedPage) return null;

    const grouped = groupRules(rules);

    return (
      <div style={styles.detailPanel}>
        <div style={styles.detailTitle}>{selectedPage.displayName}</div>
        {selectedPage.description && (
          <div style={styles.detailDescription}>{selectedPage.description}</div>
        )}

        {rulesLoading ? (
          <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading rules...</p>
        ) : rules.length === 0 ? (
          <p style={styles.noRules}>
            No rules configured. Default access applies (Admin: Edit, others: View).
          </p>
        ) : (
          <>
            {PRINCIPAL_TYPES.map((type) => {
              const groupRulesList = grouped[type] || [];
              if (groupRulesList.length === 0) return null;
              return (
                <div key={type}>
                  <div style={styles.groupHeading}>{type} Rules</div>
                  {groupRulesList.map((rule) => renderRuleRow(rule))}
                </div>
              );
            })}
          </>
        )}

        {renderAddRuleForm()}
      </div>
    );
  }

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Page Access Control</h2>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {successMsg && (
        <div style={styles.success} onClick={() => setSuccessMsg('')}>
          {successMsg} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#666' }}>Loading pages...</p>
      ) : pages.length === 0 ? (
        <p style={{ color: '#666' }}>No pages found in the registry.</p>
      ) : (
        <>
          <ul style={styles.pageList}>
            {pages.map((page) => {
              const isSelected = selectedPage?.id === page.id;
              const hasCustomRules = (rulesCount[page.id] || 0) > 0;

              return (
                <li
                  key={page.id}
                  style={isSelected ? styles.pageItemSelected : styles.pageItem}
                  onClick={() => handleSelectPage(page)}
                >
                  <div style={styles.pageInfo}>
                    <span style={styles.pageName}>{page.displayName}</span>
                    {page.description && (
                      <span style={styles.pageDescription}>{page.description}</span>
                    )}
                  </div>
                  <span
                    style={{
                      ...styles.badge,
                      ...(hasCustomRules ? styles.badgeCustom : styles.badgeDefault),
                    }}
                  >
                    {hasCustomRules
                      ? `${rulesCount[page.id]} rule${rulesCount[page.id] !== 1 ? 's' : ''}`
                      : 'Default'}
                  </span>
                </li>
              );
            })}
          </ul>

          {renderRulesPanel()}
        </>
      )}
    </div>
  );
}
